import { MOBILE_BREAKPOINT } from "@/lib/device-format";

/** Which tree of the app the user is in: chosen by the viewport, not the device. */
export type AppSurface = "mobile" | "desktop";

/** Opened as an installed app (Home Screen, desktop install) or in a browser tab. */
export type AppDisplayMode = "standalone" | "browser";

/** The super properties every PostHog event carries about where it came from. */
export type AppSurfaceProperties = {
  readonly app_surface: AppSurface;
  readonly app_display_mode: AppDisplayMode;
};

const isMobileViewport = (): boolean =>
  window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;

/**
 * Whether the app runs installed. `display-mode` covers every browser that
 * honours the manifest; `standalone` is Safari's own flag, set only in an app
 * added to the Home Screen on iOS.
 */
const appDisplayMode = (): AppDisplayMode =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true
    ? "standalone"
    : "browser";

/**
 * Where an event comes from, as PostHog super properties. Its own
 * `$device_type` reads the user agent, which is not what picks the tree: a
 * narrow desktop window gets the mobile app, a wide tablet the desktop one.
 * `isMobile` is the tree on show when known; read from the viewport otherwise
 * — before React has rendered, for the first pageview.
 */
export function appSurfaceProperties(
  isMobile: boolean = isMobileViewport(),
): AppSurfaceProperties {
  return {
    app_surface: isMobile ? "mobile" : "desktop",
    app_display_mode: appDisplayMode(),
  };
}
